import Link from 'next/link';
import { Routes } from '@/routes';
import {
  IRatingItem, IResortPathInfo, IResortReviews, IStarRating,
} from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Heading } from '@/components/ui/heading';
import { Stars } from '@/components/resort/Stars';
import {
  formatDateFromStr,
  SHORT_MONTH_WITH_YEAR_DATE_FORMAT,
} from '@/util/formatDate';

// Featured review on the Resort Overview page. One pull-quote-style
// review under a "Reviews" heading + "Browse all reviews »" link in
// the heading row — same treatment as Nearby Resorts above it.
//
// Featured-review selection logic preserved from the legacy component:
// scan reviewsList for the longest review whose average rating is
// above 2 (so we don't surface a 1-star rant in the featured slot).

const RATING_FIELDS: (keyof IStarRating)[] = [
  'familyFriendly', 'terrainPark', 'overallOther', 'intermediate',
  'expert', 'beginner', 'apresSki', 'allTerrain',
];

const getRating = (item: IRatingItem) => {
  const totalRating = RATING_FIELDS.reduce((points, field) => (item[field] ?? 0) + points, 0);
  return parseInt(`${totalRating / RATING_FIELDS.length}`, 10);
};

const getReview = (reviewsList: Array<IRatingItem>) => {
  let rating = 0;
  const review = reviewsList.reduce((prev, curr, index) => {
    if (index === 1) {
      rating = getRating(prev);
    }
    if ((curr.review.length > prev.review.length) || rating < 3) {
      const averageRating = getRating(curr);
      if (averageRating > 2) {
        rating = averageRating;
        return curr;
      }
    }
    return prev;
  });
  return { review, rating };
};

interface Props {
  pathInfo: IResortPathInfo;
  resortReviews: IResortReviews;
}

// Pull-quote in the guide is a teaser, not the full review — readers
// click "Browse all reviews »" to see the rest. Cap at 300 chars so
// the card stays roughly the same height as the surrounding sections
// regardless of how chatty the picked review happens to be.
const REVIEW_TRUNCATE_AT = 300;

export const ReviewsSection = ({ pathInfo, resortReviews }: Props) => {
  const { t } = useOTSTranslation('common');
  const { review, rating } = getReview(resortReviews.reviewsList);
  const allReviewsHref = Routes.resortTripPlanningReviews(pathInfo);
  const rawReviewBody = review.translated?.review ?? review.review;
  const reviewBody = rawReviewBody.length > REVIEW_TRUNCATE_AT
    ? `${rawReviewBody.slice(0, REVIEW_TRUNCATE_AT).trimEnd()}…`
    : rawReviewBody;
  const reviewDate = formatDateFromStr(review.date, SHORT_MONTH_WITH_YEAR_DATE_FORMAT);

  return (
    <section>
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <Heading level={2} className="font-heading text-2xl text-secondary md:text-3xl">
          {t('nav.tab.reviews')}
        </Heading>
        <Link
          href={allReviewsHref}
          className="shrink-0 text-sm font-semibold text-primary no-underline hover:underline"
        >
          {`${t('resort.reviews.allReviews')} »`}
        </Link>
      </div>
      <figure className="rounded-md border border-border bg-card p-6 md:p-8">
        <blockquote className="m-0 text-base leading-relaxed text-foreground/85 md:text-lg">
          {reviewBody}
        </blockquote>
        <figcaption className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4">
          <div>
            <div className="text-sm font-bold text-secondary">
              {`${review.firstName} ${review.lastName}`}
            </div>
            {reviewDate && (
              <div className="text-xs text-muted-foreground">{reviewDate}</div>
            )}
          </div>
          <Stars
            amount={rating}
            starsBlue
            className="flex items-center [&_svg]:h-4 [&_svg]:w-4 [&_svg]:mr-0.5 [&_svg:last-child]:mr-0"
          />
        </figcaption>
      </figure>
    </section>
  );
};
